import { FC, useState, useCallback } from 'react';
import useSwr, { mutate } from 'swr';
import toast from 'react-hot-toast';
import cn from 'clsx';

import { useAuth } from '@hooks/Auth';
import { ProxyVote } from '@utils/Proxy';
import { fetchPollDetails } from '@utils/Poll';
import {
  proxyWithdrawPoll,
  withdrawAllVotesInGroupPoll,
} from '@sdk/managers/VotesManager';
import { listProxyVotesByPoll } from '@sdk/managers/VotesManager';

export interface ProxyVotesProps {
  pollId: string;
  active?: string;
  onItemClick?: (proxy: ProxyVote, withdrew?: boolean) => void | any;
}

const ProxyVotes: FC<ProxyVotesProps> = ({ pollId, active, onItemClick }) => {
  const { dbUser, company, loading, user } = useAuth();

  const [withdrawing, setWithdrawing] = useState<string>();

  const { data: pollData } = useSwr(
    pollId && loading === false
      ? ['/poll/detail', pollId, user === null]
      : null,
    fetchPollDetails,
    {
      revalidateOnFocus: true,
    }
  );

  const { data } = useSwr(
    dbUser?.id ? ['/poll/proxyVotes', pollId, dbUser?.id] : null,
    async (_query: string, pollId: string, userId: string) => {
      if (!company) return undefined;
      const votes = (await listProxyVotesByPoll(pollId, userId)) as {
        data?: {
          result?: {
            items: ProxyVote[];
          };
        };
      };
      return votes?.data?.result?.items || [];
    },
    {
      revalidateOnFocus: false,
      onError: (err, key) => {
        console.log('Failed to fetch proxy votes', { key }, err);
        toast.error(err?.message || err?.errors?.[0]?.message);
      },
    }
  );

  const withdrawVote = useCallback(
    async (proxy: ProxyVote) => {
      if (!dbUser?.id) return toast.error('Not logged in');

      setWithdrawing(proxy.id);
      await toast.promise(
        (pollData?.subPolls?.length || 0) > 0
          ? (withdrawAllVotesInGroupPoll(
              proxy.userID,
              pollId,
              'Admin withdraw'
            ) as any)
          : proxyWithdrawPoll(
              dbUser.id,
              proxy.userID,
              pollId,
              'Proxy withdraw'
            ),
        {
          loading: 'Withdrawing vote...',
          success: 'Withdrew Proxy vote',
          error: (err) => {
            console.log('Failed to withdraw proxy vote', err);
            return err.error || err.message || 'Failed to withdraw proxy vote';
          },
        }
      );
      await mutate(['/poll/proxyVotes', pollId, dbUser.id]).catch((err) =>
        console.log('mutate', err)
      );
      onItemClick?.(proxy, true);
      if (withdrawing === proxy.id) setWithdrawing(undefined);
    },
    [dbUser, pollData, pollId, onItemClick, withdrawing]
  );

  return (
    <div className="flex flex-col space-y-2">
      {(data?.length || 0) === 0 && (
        <div className="text-center">No proxy votes</div>
      )}
      {data?.map((vote) => (
        <div
          key={`proxy-vote-${vote.pollID}`}
          className={cn('flex justify-between select-none p-1', {
            'bg-yellow text-black': active === vote.id,
          })}
          role="button"
          onClick={onItemClick ? () => onItemClick(vote) : undefined}
        >
          <div>
            {vote.user?.firstName || vote.user?.email} (
            {vote.option.title || vote.option.id})
          </div>
          <button
            className="px-2"
            disabled={withdrawing === vote.id}
            onClick={withdrawVote.bind(this, vote)}
          >
            Remove
          </button>
        </div>
      ))}
    </div>
  );
};

export default ProxyVotes;
